import { useMemo, useState } from 'react'
import { MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { usePermission } from '@/lib/permissions'
import { useFacilityLocations, useSetEquipmentRoom } from '@/hooks/useFacility'
import { formatPlace, indexUnits } from '@/lib/facilityLocation'
import { RoomPicker } from '@/components/equipment/facility/RoomPicker'
import { FacilityStudio } from '@/components/equipment/facility/FacilityStudio'

/**
 * Where a storage unit stands (z8nrz7fw25), on the unit's own page: the outside
 * of its address, Facility › Building › Floor › Room, above the inside the page
 * shows (Zone › Rack › Box). Someone who may manage equipment sets or changes
 * the room here, and can set up the facility itself from the same place.
 */
export function UnitRoomControl({ equipmentId, unitCode }: { equipmentId: string | null; unitCode: string }) {
  const { can } = usePermission()
  const canRead = can('equipment.read') || can('inventory.view')
  const canPlace = can('equipment.manage')
  const locations = useFacilityLocations(canRead && !!equipmentId)
  const setRoom = useSetEquipmentRoom()
  const [open, setOpen] = useState(false)
  const [setupOpen, setSetupOpen] = useState(false)

  const at = useMemo(
    () => (equipmentId && locations.data ? indexUnits(locations.data).get(equipmentId) : undefined),
    [equipmentId, locations.data],
  )
  if (!equipmentId || !canRead || locations.isLoading) return null
  const roomId = at?.[at.length - 1]?.id ?? null

  return (
    <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
      <MapPin className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
      {at ? (
        <span className='text-gray-800'>{formatPlace(at)}</span>
      ) : (
        <span className='text-amber-700'>Not in a room yet</span>
      )}
      {canPlace && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant='link' size='sm' className='h-auto p-0 text-sm'>
              {at ? 'Change room' : 'Set room'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align='start' className='w-80 space-y-2'>
            <p className='text-sm font-medium'>Where is {unitCode}?</p>
            <RoomPicker
              value={roomId}
              disabled={setRoom.isPending}
              onChange={(next) =>
                setRoom.mutate({ equipmentId, roomId: next }, { onSuccess: () => setOpen(false) })
              }
            />
            {setRoom.error && <p className='text-xs text-destructive'>{setRoom.error.message}</p>}
            <button
              type='button'
              className='text-xs font-medium text-primary hover:underline'
              onClick={() => {
                setOpen(false)
                setSetupOpen(true)
              }}
            >
              Set up facility, buildings and rooms
            </button>
          </PopoverContent>
        </Popover>
      )}
      <FacilityStudio open={setupOpen} onOpenChange={setSetupOpen} focusUnitId={equipmentId} />
    </div>
  )
}
